import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../ui/Table';
import { Button } from '../ui/arc/button';
import mc from '../ui/arc/metric-card.module.css';

// Piezas compartidas por Resultados y Gastos (Costos & BI).
// Todo es presentación: ningún número se calcula acá.

/** $ sin decimales, en valor absoluto (el signo lo pone quien llama). Mismo formato que tenía Resultados. */
export const money = (n: number) => '$' + Math.round(Math.abs(n)).toLocaleString('es-AR', { maximumFractionDigits: 0 });
/** Con signo − si es negativo. */
export const signed = (n: number) => (n >= 0 ? '' : '−') + money(n);

/** 'YYYY-MM' → "octubre de 2026" */
export const monthLabel = (m: string) => {
    const [y, mm] = m.split('-').map(Number);
    const s = new Date(y, mm - 1, 1).toLocaleString('es-AR', { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export type Tone = 'profit' | 'loss' | 'warn' | undefined;
export const toneText = (t: Tone) => t === 'profit' ? 'text-[var(--success)]' : t === 'loss' ? 'text-[var(--danger)]' : t === 'warn' ? 'text-[var(--warning)]' : 'text-ink';

/**
 * Tarjeta de KPI con la estética de la `metric-card` de uiarc (usa su CSS module).
 * No se usa MetricCard tal cual porque su contador animado no acepta prefijo "$", signo
 * negativo ni color por resultado, y el texto de contexto tiene que poder llevar montos en negrita.
 */
export function KpiCard({ label, value, tone, context, chip, children }: {
    label: string; value: React.ReactNode; tone?: Tone; context?: React.ReactNode; chip?: React.ReactNode; children?: React.ReactNode;
}) {
    return (
        <article className={mc.card}>
            <div className={cn(mc.top, '!mb-4')}><span>{label}</span>{chip}</div>
            <div className={cn('font-[family-name:var(--font-display)] text-[length:var(--text-2xl)] font-semibold leading-none tracking-[var(--tracking-display)] tabular-nums', toneText(tone))}>{value}</div>
            {children}
            {context && <p>{context}</p>}
        </article>
    );
}

/** Contenedor de sección: misma superficie que las tarjetas de uiarc. */
export function Panel({ title, subtitle, right, children, className, bodyClassName }: {
    title?: React.ReactNode; subtitle?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string; bodyClassName?: string;
}) {
    return (
        <section className={cn('min-w-0 rounded-[var(--radius-surface)] border border-line bg-card shadow-[var(--shadow-resting)]', className)}>
            {(title || right) && (
                <header className="flex flex-col gap-2 px-5 pt-4 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                    <div className="min-w-0">
                        {title && <h2 className="m-0 text-[length:var(--text-base)] font-semibold text-ink">{title}</h2>}
                        {subtitle && <p className="m-0 mt-0.5 text-[length:var(--text-sm)] text-faint">{subtitle}</p>}
                    </div>
                    {right}
                </header>
            )}
            <div className={cn('px-5 pb-5 pt-3', bodyClassName)}>{children}</div>
        </section>
    );
}

const selectCls = 'h-[var(--control-height-md)] rounded-[var(--radius-control)] border border-line bg-card px-3 text-[length:var(--text-sm)] font-medium text-ink outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]';

/**
 * Selector de mes con flechas ‹ › (mes anterior/siguiente dentro de la lista).
 * `months` en orden descendente (más nuevo primero). `extra` agrega opciones al principio (ej. "Todos").
 */
export function MonthPicker({ months, value, onChange, extra, label = 'Mes' }: {
    months: string[]; value: string; onChange: (v: string) => void; extra?: { value: string; label: string }[]; label?: string;
}) {
    const i = months.indexOf(value);
    const older = i >= 0 && i < months.length - 1 ? months[i + 1] : null;
    const newer = i > 0 ? months[i - 1] : null;
    return (
        <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" aria-label="Mes anterior" title="Mes anterior" disabled={!older} onClick={() => older && onChange(older)}><ChevronLeft size={16} /></Button>
            <select aria-label={label} value={value} onChange={e => onChange(e.target.value)} className={selectCls}>
                {extra?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <Button variant="ghost" size="sm" aria-label="Mes siguiente" title="Mes siguiente" disabled={!newer} onClick={() => newer && onChange(newer)}><ChevronRight size={16} /></Button>
        </div>
    );
}

/** Campo de monto con "$" adelante, estilo control de uiarc. */
export function MoneyField({ value, onChange, onBlur, onKeyDown, placeholder, id, ariaLabel, className, autoFocus }: {
    value: string | number; onChange: (v: string) => void; onBlur?: () => void; onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string; id?: string; ariaLabel?: string; className?: string; autoFocus?: boolean;
}) {
    return (
        <div className={cn('flex h-[var(--control-height-md)] items-center rounded-[var(--radius-control)] border border-line bg-card px-3 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--focus-ring)]', className)}>
            <span className="mr-1.5 text-[length:var(--text-sm)] font-semibold text-faint">$</span>
            <input id={id} aria-label={ariaLabel} type="number" min={0} inputMode="decimal" autoFocus={autoFocus}
                value={value} placeholder={placeholder}
                onChange={e => onChange(e.target.value)} onBlur={onBlur} onKeyDown={onKeyDown}
                className="w-full min-w-0 border-0 bg-transparent p-0 text-right text-[length:var(--text-sm)] font-semibold tabular-nums text-ink outline-none" />
        </div>
    );
}
